import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Stack, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { useIntl } from "react-intl";
import AlertDialog from "components/common/AlertDialog";
import RevaText from "components/common/RevaText";
import MatchScoringEditor, { SwitchRow } from "./MatchScoringEditor";
import { DEFAULT_MATCH_SCORING, normalizeMatchScoring } from "./matchScoring";

/**
 * "Inherit or customize" form shared by the event page and the tournament settings.
 * - `matchScoring`: the stored config, or null when inheriting.
 * - `inheritedOptions`: what applies while inheriting, shown read-only as
 *   [{ key, label, tabLabel?, config }]. More than one (an event with tournaments of sports
 *   with different defaults) shows a tab per option.
 * - `inheritedBadgeId`: where the inherited rule comes from ("Reglas del deporte activas").
 * - `onSave(config | null)`: must return a promise.
 */
const MatchScoringForm = ({
  matchScoring,
  inheritedOptions = [],
  inheritedBadgeId = null,
  inheritedNoteId = null,
  presets,
  inheritLabelId,
  inheritHelpId,
  lockedMessage = null,
  hasClosedGroupMatches = false,
  onSave,
}) => {
  const intl = useIntl();
  const [inherit, setInherit] = useState(!matchScoring);
  const [selectedKey, setSelectedKey] = useState(inheritedOptions[0]?.key);
  const selectedOption = inheritedOptions.find((option) => option.key === selectedKey) || inheritedOptions[0];
  const [draft, setDraft] = useState(normalizeMatchScoring(matchScoring || selectedOption?.config));
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setInherit(!matchScoring);
    setDraft(normalizeMatchScoring(matchScoring || inheritedOptions[0]?.config || DEFAULT_MATCH_SCORING));
    setSelectedKey(inheritedOptions[0]?.key);
  }, [matchScoring, inheritedOptions]);

  const locked = !!lockedMessage;

  const handleInheritChange = (checked) => {
    setInherit(checked);
    // Start customizing from the rule that applies today.
    if (!checked && !matchScoring && selectedOption) setDraft(normalizeMatchScoring(selectedOption.config));
  };

  const save = async () => {
    setConfirmOpen(false);
    setSaving(true);
    try {
      await onSave(inherit ? null : draft);
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    if (hasClosedGroupMatches) {
      setConfirmOpen(true);
      return;
    }
    save();
  };

  return (
    <Stack spacing={3}>
      {locked && <Alert severity="info">{lockedMessage}</Alert>}
      {!locked && hasClosedGroupMatches && (
        <Alert severity="warning">{intl.formatMessage({ id: "matchScoring.closedMatchesWarning" })}</Alert>
      )}

      <Box sx={(theme) => ({ pb: 3, borderBottom: `1px solid ${theme.palette.divider}` })}>
        <SwitchRow
          titleId={inheritLabelId}
          descriptionId={inheritHelpId}
          checked={inherit}
          disabled={locked}
          onChange={handleInheritChange}
        />
      </Box>

      {inherit && inheritedOptions.length > 1 && (
        <Stack spacing={1}>
          {inheritedNoteId && <RevaText id={inheritedNoteId} variant="body2" color="text.secondary" />}
          <ToggleButtonGroup
            exclusive
            size="small"
            value={selectedOption?.key}
            onChange={(_, key) => key && setSelectedKey(key)}
            sx={{ flexWrap: "wrap" }}
          >
            {inheritedOptions.map((option) => (
              <ToggleButton key={option.key} value={option.key} sx={{ textTransform: "none" }}>
                {option.tabLabel || option.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>
      )}

      {inherit ? (
        <MatchScoringEditor
          readOnly
          value={selectedOption?.config || DEFAULT_MATCH_SCORING}
          readOnlyLabel={selectedOption?.label}
          readOnlyBadgeId={inheritedBadgeId}
          presets={presets}
        />
      ) : (
        <MatchScoringEditor value={draft} onChange={setDraft} presets={presets} readOnly={locked} />
      )}

      {!locked && (
        <Box>
          <Button variant="contained" size="large" onClick={handleSave} disabled={saving}>
            {intl.formatMessage({ id: "Guardar" })}
          </Button>
        </Box>
      )}

      <AlertDialog
        hideButton
        isOpen={confirmOpen}
        alertTitle={intl.formatMessage({ id: "matchScoring.confirmTitle" })}
        alertBody={intl.formatMessage({ id: "matchScoring.confirmBody" })}
        confirmButtonTitle={intl.formatMessage({ id: "Guardar" })}
        cancelButtonTitle={intl.formatMessage({ id: "Cancelar" })}
        onConfirm={save}
        onCancel={() => setConfirmOpen(false)}
      />
    </Stack>
  );
};

export default MatchScoringForm;
